import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronDown, Pencil, Check, X, Package, CheckSquare, Square, FolderInput } from "lucide-react";
import { CategoryPicker } from "../../components/extras/CategoryPicker";
import { CategoryOrder } from "../../components/extras/CategoryOrder";
import { toast } from "sonner";
import { http, errMsg } from "../../lib/api";
import { media, aud } from "../../components/extras/ExtrasShell";
import { SyncPanel } from "../../components/extras/SyncPanel";

const SHORT = { kit: "KIT", checklist: "CHECKLIST", itinerary: "ITINERARY", bundle: "BUNDLE" };

const Card = ({ p, picking, picked, onPick }) => (
  <Link to={`/extras/products/${p.id}`} onClick={(e) => { if (picking) { e.preventDefault(); onPick(p.id); } }} data-testid={`ex-card-${p.id}`} className={`ex-card ex-fade overflow-hidden group hover:shadow-[0_10px_30px_-12px_rgba(48,39,29,.25)] transition-shadow relative ${picked ? "ring-2 ring-[#8B6B4A]" : ""}`}>
    {picking && <span data-testid={`ex-card-pick-${p.id}`} className="absolute top-2 left-2 z-10 bg-white/90 rounded p-0.5">{picked ? <CheckSquare size={18} className="text-[#8B6B4A]" /> : <Square size={18} className="text-stone-500" />}</span>}
    <div className="aspect-square bg-[#EEF1F4] overflow-hidden">
      {p.image_id && <img src={media(p.image_id)} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />}
    </div>
    <div className="p-4">
      <div className="flex items-center justify-between">
        <span className="ex-mono text-[10px] tracking-[0.18em] text-stone-500">{SHORT[p.product_type]} · ED. {p.edition || 1}</span>
        <span className={`ex-mono text-[9px] tracking-[0.16em] px-1.5 py-0.5 rounded border ${p.status === "generating" ? "text-amber-700 border-amber-200 bg-amber-50" : p.status === "failed" ? "text-red-700 border-red-200 bg-red-50" : p.published ? "text-emerald-700 border-emerald-200 bg-emerald-50" : "text-stone-500 border-stone-200 bg-stone-50"}`}>
          {p.status === "generating" ? "MAKING" : p.status === "failed" ? "STOPPED" : p.published ? "PUBLISHED" : "DRAFT"}
        </span>
      </div>
      <div className="ex-display text-lg leading-snug mt-2 line-clamp-2">{p.listing?.name || p.content?.title || p.brief}</div>
      <div className="text-sm text-stone-600 mt-1">{p.listing ? aud(p.listing.price_aud) : p.progress}</div>
    </div>
  </Link>
);

const Group = ({ name, items, open, toggle, onRename, pick }) => {
  const [edit, setEdit] = useState(null);
  return (
    <section data-testid={`ex-group-${name}`} className="mb-10">
      <div className="flex items-center gap-3 border-b border-[#E6DDCF] pb-3 mb-5">
        <button data-testid={`ex-group-toggle-${name}`} onClick={toggle} className="flex items-center gap-3 text-left">
          <ChevronDown size={18} className={`text-stone-500 transition-transform ${open ? "" : "-rotate-90"}`} />
          {edit === null && <span className="ex-display text-2xl">{name}</span>}
        </button>
        {edit !== null && <>
          <input data-testid={`ex-group-rename-input-${name}`} value={edit} onChange={(e) => setEdit(e.target.value)} className="ex-display text-2xl bg-transparent border-b border-stone-400 outline-none" autoFocus />
          <button data-testid={`ex-group-rename-save-${name}`} onClick={async () => { await onRename(name, edit); setEdit(null); }}><Check size={16} /></button>
          <button onClick={() => setEdit(null)}><X size={16} /></button>
        </>}
        <span className="ex-mono text-xs text-stone-500">{items.length} products · {items.filter((p) => p.published).length} live</span>
        {pick.on && <button data-testid={`ex-group-pickall-${name}`} onClick={() => pick.all(items.map((p) => p.id))} className="text-xs text-[#8B6B4A] hover:underline">Select all in group</button>}
        <button data-testid={`ex-group-rename-${name}`} onClick={() => setEdit(name)} className="ml-auto text-stone-500 hover:text-stone-800"><Pencil size={15} /></button>
      </div>
      {open && <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">{items.map((p) => <Card key={p.id} p={p} picking={pick.on} picked={pick.sel.includes(p.id)} onPick={pick.toggle} />)}</div>}
    </section>
  );
};

export default function ExtrasLibrary() {
  const [rows, setRows] = useState(null);
  const [params] = useSearchParams();
  const filter = params.get("type") || "all";
  const [open, setOpen] = useState({});
  const [order, setOrder] = useState([]);
  const load = () => Promise.all([http.get("/extras/products"), http.get("/extras/collections/order")])
    .then(([r, o]) => { setRows(r.data); setOrder(o.data); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);
  const groups = useMemo(() => {
    const g = {};
    (rows || []).filter((p) => filter === "all" || p.product_type === filter).forEach((p) => { (g[p.collection_name] = g[p.collection_name] || []).push(p); });
    const at = (n) => (order.includes(n) ? order.indexOf(n) : 999);
    return Object.entries(g).sort(([a], [b]) => at(a) - at(b));
  }, [rows, filter, order]);
  const counts = useMemo(() => (rows || []).reduce((c, p) => ({ ...c, [p.collection_name]: (c[p.collection_name] || 0) + 1 }), {}), [rows]);
  const isOpen = (n) => open[n] !== false;
  const [picking, setPicking] = useState(false);
  const [sel, setSel] = useState([]);
  const pick = { on: picking, sel, toggle: (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id])), all: (ids) => setSel((s) => [...new Set([...s, ...ids])]) };
  const move = async (c) => {
    try { const { data } = await http.post("/extras/collections/move", { ids: sel, collection: c }); toast.success(`Moved ${data.moved} product(s) to ${c}`); setSel([]); setPicking(false); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const rename = async (old, nw) => {
    try { await http.post("/extras/collections/rename", { old, new: nw.trim() }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="ex-library">
      <div className="flex flex-wrap items-end gap-6 mb-10">
        <div>
          <div className="ex-eyebrow mb-3">Product library</div>
          <h1 className="ex-display text-4xl sm:text-5xl lg:text-6xl font-semibold">Everything you've made</h1>
        </div>
        <Link to="/extras/bundles/new" data-testid="ex-new-bundle" className="ml-auto ex-btn ex-btn-line"><Package size={15} /> New bundle</Link>
      </div>
      <SyncPanel count={rows?.length} onDone={load} />
      {rows && rows.length > 0 && (
        <div className="flex justify-end items-center gap-4 text-sm text-stone-500 mb-4">
          <CategoryOrder counts={counts} onSaved={load} />
          <button data-testid="ex-pick-toggle" onClick={() => { setPicking(!picking); setSel([]); }} className={`mr-auto order-first inline-flex items-center gap-1.5 ${picking ? "text-stone-900 font-medium" : "hover:text-stone-900"}`}><FolderInput size={15} /> {picking ? "Cancel" : "Change category for several"}</button>
          <button data-testid="ex-expand-all" onClick={() => setOpen({})} className="hover:text-stone-900">Expand all</button>
          <button data-testid="ex-collapse-all" onClick={() => setOpen(Object.fromEntries(groups.map(([n]) => [n, false])))} className="hover:text-stone-900">Collapse all</button>
        </div>
      )}
      {picking && <div data-testid="ex-move-bar" className="sticky top-16 z-20 ex-card px-5 py-3 mb-6 flex flex-wrap items-center gap-3 text-sm shadow-sm">
        <span data-testid="ex-move-count">{sel.length} selected</span>
        <span className="text-stone-500">Tick products below, then move them to</span>
        {sel.length > 0 ? <CategoryPicker testid="ex-move-category" onPick={move} placeholder="Choose category…" /> : <span className="text-stone-400 text-xs">(select at least one)</span>}
      </div>}
      {rows === null && <div className="text-stone-500">Loading…</div>}
      {groups.map(([n, items]) => <Group key={n} name={n} items={items} open={isOpen(n)} toggle={() => setOpen((o) => ({ ...o, [n]: !isOpen(n) }))} onRename={rename} pick={pick} />)}
      {rows && rows.length > 0 && groups.length === 0 && <div className="text-stone-500">Nothing of this type yet.</div>}
    </div>
  );
}
